import { useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";

import { apiTokenRequest } from "../auth/msalConfig";

/**
 * WFP API client.
 *
 * Provides a `useApi()` hook that returns a `callApi(path, init?)` function.
 * The function silently acquires a token for the WFP API scope (falling
 * back to popup if interaction is required), attaches it as a Bearer
 * token, and dispatches a fetch.
 *
 * Base URL is read from `import.meta.env.VITE_API_BASE_URL`, falling back
 * to the local Functions runtime at port 7071 for `npm run dev` against a
 * locally-running API.
 *
 * See ../../whanau-factor-api/ (the API repo) and ADR-001 §2 "Identity"
 * for the trust-chain context.
 */

const DEFAULT_API_BASE = "http://localhost:7071/api/v1";

const API_BASE: string =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? DEFAULT_API_BASE;

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export function useApi() {
  const { instance } = useMsal();

  // useCallback gives the returned function a stable reference across the
  // consumer's renders. Without this, `callApi` is a fresh function on every
  // render, which silently breaks effects/memos that include it in deps
  // (they refire/recompute on every render → render loop on any setState
  // path that triggers a re-render). The function captures `instance` from
  // the MSAL context, which is itself stable, so the dep array is safe.
  return useCallback(
    async function callApi<T>(path: string, init?: RequestInit): Promise<T> {
      const account = instance.getActiveAccount();
      if (!account) {
        throw new Error("Not signed in — cannot call WFP API");
      }

      let accessToken: string;
      try {
        const result = await instance.acquireTokenSilent({
          ...apiTokenRequest,
          account,
        });
        accessToken = result.accessToken;
      } catch (err) {
        if (err instanceof InteractionRequiredAuthError) {
          // Fall back to popup if silent acquisition fails (e.g. consent needed).
          const result = await instance.acquireTokenPopup({
            ...apiTokenRequest,
            account,
          });
          accessToken = result.accessToken;
        } else {
          throw err;
        }
      }

      const url = path.startsWith("http") ? path : `${API_BASE}${path}`;

      const response = await fetch(url, {
        ...init,
        headers: {
          ...init?.headers,
          accept: "application/json",
          authorization: `Bearer ${accessToken}`,
        },
      });

      const contentType = response.headers.get("content-type") ?? "";
      const body: unknown = contentType.includes("application/json")
        ? await response.json().catch(() => null)
        : await response.text().catch(() => null);

      if (!response.ok) {
        throw new ApiError(
          response.status,
          body,
          `WFP API ${response.status} ${response.statusText}`,
        );
      }

      return body as T;
    },
    [instance],
  );
}

export type HealthResponse = {
  ok: boolean;
  user: {
    oid: string;
    name: string;
    upn?: string;
  };
  db: {
    connected: boolean;
    error?: string;
  };
  timestamp: string;
};

/** A row from /api/v1/whanau. */
export type Whanau = {
  whanau_id: number;
  display_name: string;
  service_id: string;
  engagement_state: string;
  registered_at: string;
  primary_iwi: string | null;
  primary_location: string | null;
  current_keyworker_count: number;
};

export type WhanauListResponse = {
  rows: Whanau[];
  count: number;
  scope: { kaimahi_oid: string };
};

/** A row from /api/v1/whanau/closed. */
export type ClosedWhanau = {
  whanau_id: number;
  display_name: string;
  service_id: string;
  engagement_state: string;
  exited_at: string | null;
  exited_reason: string | null;
  /** 'declined' = never engaged; 'exit-completed' = exit assessment recorded. */
  closed_reason: "declined" | "exit-completed";
  registered_at: string;
  primary_iwi: string | null;
  primary_location: string | null;
  current_keyworker_count: number;
};

export type ClosedWhanauListResponse = {
  rows: ClosedWhanau[];
  count: number;
  scope: { kaimahi_oid: string };
};

/** A row from /api/v1/whanau/:id — full whānau metadata. */
export type WhanauDetail = {
  whanau_id: number;
  display_name: string;
  service_id: string;
  engagement_state: string;
  registered_at: string;
  primary_iwi: string | null;
  primary_location: string | null;
  exited_at: string | null;
  exited_reason: string | null;
};

/** A member of a whānau. */
export type WhanauMember = {
  whanau_member_id: number;
  exess_person_id: number | null;
  display_name: string;
  age_band: string | null;
  gender: string | null;
  relationship: string | null;
  is_primary_contact: boolean;
  member_notes: string | null;
  /** When this member was first recorded (valid_from from the temporal table). */
  recorded_at: string;
};

/**
 * A subdomain's current assessment state. One per subdomain (18 total);
 * fields are null when the subdomain has never been assessed for this whānau.
 */
export type SubdomainAssessmentRow = {
  subdomain_id: string;
  domain_id: string;
  pou_id: string;
  name_en: string;
  sort_order: number;
  assessment_id: number | null;
  session_id: number | null;
  state_kind: "risk" | "change" | "protective" | "not-assessed" | null;
  not_assessed_reason_id: string | null;
  kaimahi_notes: string | null;
  shared_notes: string | null;
  cycle_label: string | null;
  recorded_at: string | null;
  recorded_by_oid: string | null;
  /** Immediately-previous state for this subdomain, or null if first assessment. */
  previous_state_kind: "risk" | "change" | "protective" | "not-assessed" | null;
  previous_recorded_at: string | null;
};

/** A firing alert on a whānau profile — derived from framework_alert_definition. */
export type ProfileAlert = {
  alert_id: string;
  name_en: string;
  description: string | null;
  subdomain_id: string;
  subdomain_name_en: string;
  trigger_state: string;
  severity: "info" | "warn" | "urgent";
  sort_order: number;
};

/** A session summary in the profile response — one row per assessment_session. */
export type SessionSummary = {
  session_id: number;
  cycle_label: string;
  session_at: string;
  facilitator_oid: string;
  setting: string | null;
  recorded_at: string;
  assessment_count: number;
  /** Sign-off status — drives wizard pre-flight gating. */
  signoff_status: "pending" | "approved" | "returned" | null;
};

/** Current resolved keyworker for a whānau. */
export type ProfileKeyworker = {
  kaimahi_oid: string;
  upn: string;
  display_name: string | null;
};

/**
 * Client-facing tracked status for a derived recommendation (ADR-009). The API
 * maps the stored 'reopened' action back to 'open'.
 */
export type RecommendationStatusValue = "open" | "actioned" | "dismissed";

/** Current tracked status for one derived recommendation, from the profile. */
export type RecommendationStatusRow = {
  recommendation_key: string;
  subdomain_id: string | null;
  status: RecommendationStatusValue;
  note: string | null;
  acted_by_oid: string;
  acted_at: string;
};

/** Request body for POST /api/v1/whanau/:id/recommendations. */
export type RecommendationActionRequest = {
  recommendation_key: string;
  action: "actioned" | "dismissed" | "reopened";
  note: string | null;
  subdomain_id: string | null;
  state_kind_at_action: "risk" | "change" | null;
};

/** A service a whānau is attached to (whanau_service link). */
export type ServiceRef = { service_id: string; name_en: string };

export type WhanauProfileResponse = {
  whanau: WhanauDetail;
  members: WhanauMember[];
  assessments: SubdomainAssessmentRow[];
  alerts: ProfileAlert[];
  sessions: SessionSummary[];
  keyworkers: ProfileKeyworker[];
  /**
   * Tracked recommendation statuses (ADR-009). Optional — an older API build
   * (deployed before the SPA) omits it; the UI treats missing as "none yet".
   */
  recommendations?: RecommendationStatusRow[];
  /**
   * Every service the whānau is attached to (whanau_service). Optional — an
   * older API build omits it; the UI falls back to whanau.service_id.
   */
  services?: ServiceRef[];
};

/** Full session detail (from /whanau/:id/assessment-sessions/:sessionId). */
export type SessionDetail = {
  session_id: number;
  whanau_id: number;
  cycle_label: string;
  session_at: string;
  facilitator_oid: string;
  setting: string | null;
  session_notes: string | null;
  shared_summary: string | null;
  recorded_at: string;
};

export type SessionAssessmentRow = {
  assessment_id: number;
  subdomain_id: string;
  domain_id: string;
  pou_id: string;
  name_en: string;
  sort_order: number;
  state_kind: "risk" | "change" | "protective" | "not-assessed";
  not_assessed_reason_id: string | null;
  kaimahi_notes: string | null;
  shared_notes: string | null;
  recorded_at: string;
};

export type SessionDetailResponse = {
  session: SessionDetail;
  assessments: SessionAssessmentRow[];
  access: { via: "keyworker" | "role-override"; role: string };
};

/** Cycle labels — kept in sync with src/framework/types.ts CycleLabelId. */
export type CycleLabel = "entry" | "midpoint" | "exit" | "follow-up";

/** Who completed an assessment (ADR-012). Mirrors the API CompletionMode. */
export type CompletionMode = "kaimahi" | "whanau" | "collaborative";

/** A selectable supervisor for the sign-off dropdown (ADR-013). */
export type Supervisor = {
  oid: string;
  upn: string | null;
  displayName: string | null;
};

export type SupervisorListResponse = {
  rows: Supervisor[];
  count: number;
};

/** Request body shape for POST /api/v1/whanau/:id/assessment-sessions. */
export type AssessmentSessionRequest = {
  cycle_label: CycleLabel;
  /** Who completed the assessment (ADR-012). Required. */
  completed_by: CompletionMode;
  session_at: string; // ISO 8601
  setting: string | null;
  session_notes: string | null;
  shared_summary: string | null;
  /** Selected supervisor (ADR-013): manager/exec who signs off. Required. */
  supervisor_oid: string;
  assessments: Array<{
    subdomain_id: string;
    state_kind: "risk" | "change" | "protective" | "not-assessed";
    not_assessed_reason_id:
      | "not-applicable"
      | "declined"
      | "not-yet-assessed"
      | null;
    kaimahi_notes: string | null;
    shared_notes: string | null;
  }>;
};

export type AssessmentSessionResponse = {
  session_id: number;
  assessment_count: number;
  assessment_ids: number[];
  access: { via: "keyworker" | "role-override"; role: string };
};

/** Response from GET /api/v1/me/share-pin. */
export type SharePinStatusResponse = {
  has_pin: boolean;
  share_pin_set_at: string | null;
  locked_until: string | null;
  failed_attempts: number;
};

/** Role identifiers — kept in sync with src/auth/roles.ts in the API. */
export type RoleId =
  | "kaimahi"
  | "manager"
  | "exec-manager"
  | "administrator"
  | "super-admin";

/** A row from /api/v1/admin/states — per-state colour + icon overrides. */
export type StateSetting = {
  state_id: "risk" | "change" | "protective" | "not-assessed";
  colour: string | null;
  icon_svg: string | null;
  updated_at: string;
};

export type StateListResponse = {
  rows: StateSetting[];
  count: number;
};

/** A row from /api/v1/admin/tiles — current tile content overrides. */
export type TileSetting = {
  tile_id: string;
  title: string | null;
  description: string | null;
  photo_url: string | null;
  updated_at: string;
};

export type TileListResponse = {
  rows: TileSetting[];
  count: number;
};

/** Response from /api/v1/admin/users/lookup — resolved Entra user. */
export type UserLookupResponse = {
  oid: string;
  display_name: string;
  mail: string;
};

/** One row in the /api/v1/admin/users/search results. */
export type UserSearchResult = {
  oid: string;
  display_name: string;
  mail: string;
};

export type UserSearchResponse = {
  rows: UserSearchResult[];
  count: number;
};

/** A row from /api/v1/admin/framework — a domain or subdomain added by an admin. */
export type FrameworkAdditionRow = {
  kind: "domain" | "subdomain";
  id: string;
  name_en: string;
  name_mi: string | null;
  domain_id: string | null;
  pou_id: string | null;
  toc_outcome: string | null;
  sort_order: number;
  updated_at: string;
};

/** A row from /api/v1/admin/framework — an override on a code-default domain or subdomain. */
export type FrameworkOverrideRow = {
  kind: "domain" | "subdomain";
  id: string;
  name_en: string | null;
  name_mi: string | null;
  /** Non-null = admin-supplied ToC string overriding the seed (subdomains only). */
  toc_outcome: string | null;
  hidden: boolean;
  updated_at: string;
};

export type FrameworkOverlayResponse = {
  additions: FrameworkAdditionRow[];
  overrides: FrameworkOverrideRow[];
};

/** Manager caseload assignment row. */
export type CaseloadAssignment = {
  whanau_id: number;
  whanau_display_name: string | null;
  exess_service_id: number;
  service_label: string;
  is_key_worker: boolean;
  source: "exess" | "override-add";
  exess_client_worker_id: number | null;
  override_id: number | null;
};

/** Resolved-kaimahi bucket in the caseload roster. */
export type CaseloadKaimahi = {
  kaimahi_oid: string;
  upn: string;
  display_name: string | null;
  assignments: CaseloadAssignment[];
};

/** Unresolved Exess worker (no matching directory entry yet). */
export type CaseloadUnresolved = {
  worker_upn: string | null;
  worker_first_names: string | null;
  worker_last_name: string | null;
  exess_worker_id: number;
  assignments: Array<Omit<CaseloadAssignment, "source" | "override_id">>;
};

export type CaseloadResponse = {
  kaimahi: CaseloadKaimahi[];
  unresolved: CaseloadUnresolved[];
  counts: {
    kaimahi_resolved: number;
    assignments_resolved: number;
    workers_unresolved: number;
    assignments_unresolved: number;
  };
};

export type CaseloadProvisionResult = {
  upn: string;
  status: "provisioned" | "already-known" | "not-found" | "error";
  oid: string | null;
  display_name: string | null;
  message?: string;
};

export type CaseloadProvisionResponse = {
  results: CaseloadProvisionResult[];
  summary: {
    provisioned: number;
    already_known: number;
    not_found: number;
    errors: number;
  };
};

/** A row from /api/v1/manager/signoffs — supervisor sign-off queue. */
export type SignoffRow = {
  signoff_id: number;
  whanau_id: number;
  whanau_display_name: string | null;
  assessment_session_id: number | null;
  cycle_label: string | null;
  status: "pending" | "approved" | "returned";
  requested_at: string;
  requested_by_oid: string;
  requested_by_display_name: string | null;
  reviewed_at: string | null;
  reviewed_by_oid: string | null;
  reviewed_by_display_name: string | null;
  review_notes: string | null;
  session_setting: string | null;
  session_at: string | null;
};

export type SignoffListResponse = {
  rows: SignoffRow[];
  count: number;
  scope: { status: string; limit: number };
};

/** Response from /api/v1/me — caller identity + effective role + view prefs. */
export type MeResponse = {
  user: {
    oid: string;
    name: string;
    upn?: string;
  };
  role: {
    roleId: RoleId;
    level: number;
    nameEn: string;
    isExplicit: boolean;
  };
  view_preferences?: {
    home_tiles_hidden: string[];
  };
  /** Effective permission flags for the caller, resolved server-side. */
  permissions?: Record<string, boolean>;
};

/** A row in the staff-views editor — one per kaimahi we know about. */
export type ViewPreferenceRow = {
  kaimahi_oid: string;
  display_name: string | null;
  upn: string | null;
  role_id: RoleId;
  role_level: number;
  hidden_tiles: string[];
  /** Whether the caller is permitted to edit this row (server says so). */
  editable: boolean;
};

export type ViewPreferenceListResponse = {
  rows: ViewPreferenceRow[];
  count: number;
  tile_catalogue: string[];
  caller_role_level: number;
};

/**
 * GET /api/v1/whanau/:id/wizard-draft response.
 *
 * The `draft` payload is opaque to the API — it's whatever the SPA wizard
 * stored. The wizard knows its own shape; treat as `unknown` and validate
 * narrowly at hydration time.
 */
export type WizardDraftResponse = {
  draft: unknown;
  updated_at: string;
};

/** One row in /api/v1/admin/roles — an explicit role assignment. */
export type RoleAssignment = {
  kaimahi_oid: string;
  display_name: string | null;
  role_id: RoleId;
  level: number;
  name_en: string;
  name_mi: string | null;
  /** null when the row is implicit (default 'kaimahi' role, no explicit assignment). */
  assigned_at: string | null;
  /** '' (empty string) for implicit default-role rows. */
  assigned_by_oid: string;
  assignment_notes: string | null;
};

export type RoleListResponse = {
  rows: RoleAssignment[];
  count: number;
};

/** Known event_type values for the audit log. Mirrors the API helper. */
export type AuditEventType =
  | "whanau.viewed"
  | "whanau.viewed.override"
  | "whanau.registered"
  | "whanau.exited"
  | "keyworker.assigned"
  | "keyworker.ended"
  | "keyworker.reassigned"
  | "assessment.recorded"
  | "assessment.recorded.override"
  | "assessment.corrected"
  | "recommendation.actioned"
  | "recommendation.dismissed"
  | "recommendation.reopened"
  | "signoff.requested"
  | "signoff.approved"
  | "signoff.returned"
  | "role.assigned"
  | "role.revoked"
  | "access.denied"
  | "export.csv"
  | "export.pdf"
  | "exess.sync.imported"
  | "reports.viewed";

export type AuditEventRow = {
  event_id: number;
  occurred_at: string;
  actor_oid: string;
  event_type: AuditEventType | string;
  whanau_id: number | null;
  detail: unknown;
  correlation_id: string | null;
};

export type AuditListResponse = {
  rows: AuditEventRow[];
  count: number;
  total: number;
  scope: {
    from: string;
    to: string;
    event_types: string[];
  };
};
